import Phaser from "phaser";
import { TREASURY_ART } from "../config/treasuryArt";
import { treasuryHourglassMotion } from "./treasuryHourglassMotion";

/** Decorative activity, not a fabricated estimate of blockchain completion. */
export function createTreasuryHourglass(parent: HTMLElement, prefersReduced: boolean) {
  const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
  let reduced = prefersReduced || motion.matches;
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  const canvas = document.createElement("canvas"); canvas.setAttribute("aria-hidden", "true");
  class Hourglass extends Phaser.Scene {
    sand?: Phaser.GameObjects.Graphics;
    body?: Phaser.GameObjects.Container;
    elapsed = 0;
    preload() { this.load.image("hourglass", TREASURY_ART.hourglass); }
    create() {
      if (!this.textures.exists("hourglass")) return;
      const frame = this.add.image(0, 0, "hourglass").setDisplaySize(140, 256);
      this.sand = this.add.graphics();
      this.body = this.add.container(this.scale.width / 2, this.scale.height / 2, [frame, this.sand]);
      this.layout(); this.scale.on("resize", () => this.layout());
      this.game.events.once(Phaser.Core.Events.POST_RENDER, () => { parent.dataset.ready = "true"; });
    }
    layout() {
      this.body?.setPosition(this.scale.width / 2, this.scale.height / 2)
        .setScale(Math.min(this.scale.width / 294, this.scale.height / 280));
    }
    override update(_time: number, delta: number) {
      if (!this.sand || !this.body) return;
      if (!reduced) this.elapsed += Math.min(delta, 100);
      const { phase: t, fill, rotation, sandRotation } = treasuryHourglassMotion(this.elapsed, reduced);
      this.body.rotation = rotation;
      this.sand.rotation = sandRotation;
      const g = this.sand.clear();
      // Mirror-identical full bulbs preserve every sand vertex at each flip.
      // Swap local bulb orientation only after the half-turn is complete.
      const remaining = Math.sqrt(1 - fill), top = -5 - remaining * 63, half = remaining * 28;
      const bottom = 68 - fill * 63;
      g.fillStyle(0xf2c563, .92);
      if (fill < 1) g.fillTriangle(-half, top, half, top, 0, -5);
      if (fill > 0) g.fillTriangle(-28, 68, 28, 68, 0, bottom);
      if (fill < 1) {
        const opacity = Math.min(1, fill * 30, (1 - fill) * 30);
        g.fillStyle(0xffdd93, .8 * opacity).fillRect(-.8, -4, 1.6, bottom + 5);
        for (let i = 0; i < 9; i++) {
          const y = -3 + ((t / 330 + i / 9) % 1) * (bottom + 3);
          g.fillStyle(0xfff1c6, .9 * opacity).fillCircle(Math.sin(i * 2.4) * 1.1, y, .8);
        }
      }
    }
  }
  const game = new Phaser.Game({ type: Phaser.CANVAS, parent, canvas, transparent: true, banner: false,
    width: Math.max(1, parent.clientWidth * ratio), height: Math.max(1, parent.clientHeight * ratio),
    audio: { noAudio: true }, input: { activePointers: 0 }, fps: { target: 30 }, scene: [new Hourglass()] });
  const resize = new ResizeObserver(() => game.scale.resize(Math.max(1, parent.clientWidth * ratio), Math.max(1, parent.clientHeight * ratio)));
  resize.observe(parent);
  const visibility = () => { if (document.hidden) game.loop.sleep(); else game.loop.wake(); };
  const reduce = () => { reduced = prefersReduced || motion.matches; };
  motion.addEventListener("change", reduce); document.addEventListener("visibilitychange", visibility);
  return { game, destroy() {
    resize.disconnect(); motion.removeEventListener("change", reduce); document.removeEventListener("visibilitychange", visibility);
    game.destroy(true); canvas.remove(); delete parent.dataset.ready;
  } };
}
